import { useState } from "react";
import { supabase } from "../supabaseClient";
import { notify } from "../components/UI/DesignStandards/Notifications/notify";

/**
 * Hook to manage OTP flow for password operations
 * @param {string} userEmail - User's email address
 * @returns {Object} OTP flow state and handlers
 */
export const useOTPFlow = (userEmail) => {
  const [requireOTP, setRequireOTP] = useState(false);
  const [otpCodeSent, setOtpCodeSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpCodeValid, setOtpCodeValid] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpError, setOtpError] = useState(null);

  /**
   * Send OTP code to user's email
   */
  const sendOTP = async () => {
    if (!userEmail) {
      setOtpError("Email address is required");
      return;
    }

    setSendingOTP(true);
    setOtpError(null);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: userEmail,
        options: {
          shouldCreateUser: false, // Don't create user, just send OTP
        },
      });

      if (otpError) {
        throw otpError;
      }

      setRequireOTP(true);
      setOtpCodeSent(true);
      notify.success("Verification code sent to your email");
    } catch (error) {
      console.error("Error sending OTP:", error);

      // Check for rate limiting errors
      const errorMessage = error?.message || error?.toString() || "";
      let userFriendlyMessage =
        "Failed to send verification code. Please try again.";

      // Check if it's a rate limit error (429 or contains rate limit message)
      if (
        error?.status === 429 ||
        errorMessage.toLowerCase().includes("rate limit") ||
        errorMessage.toLowerCase().includes("too many requests") ||
        errorMessage.toLowerCase().includes("you can only request this after")
      ) {
        // Try to extract wait time from error message (e.g., "after 27 seconds")
        const waitTimeMatch = errorMessage.match(/after (\d+) seconds?/i);
        if (waitTimeMatch) {
          const seconds = parseInt(waitTimeMatch[1], 10);
          userFriendlyMessage = `Please wait ${seconds} seconds before requesting another verification code.`;
        } else {
          userFriendlyMessage =
            "Too many requests. Please wait a moment before requesting another verification code.";
        }
      }

      setOtpError(userFriendlyMessage);
      notify.error(userFriendlyMessage);
    } finally {
      setSendingOTP(false);
    }
  };

  /**
   * Handle OTP code input - verify when 6 digits are entered
   * @param {string} code - The OTP code entered by user
   */
  const handleOTPCodeChange = async (code) => {
    setOtpCode(code);
    // Clear error when user starts typing a new code
    if (code.length < 6) {
      setOtpError(null);
      setOtpCodeValid(false);
      return;
    }

    // Validate format: 6 digits
    const isValid = /^\d{6}$/.test(code);
    if (!isValid) {
      setOtpCodeValid(false);
      return;
    }

    // Format is valid - now verify the OTP code
    setOtpCodeValid(false); // Set to false while verifying
    setOtpError(null);

    try {
      const { data: verifyData, error: verifyError } =
        await supabase.auth.verifyOtp({
          email: userEmail,
          token: code,
          type: "email",
        });

      if (verifyError || !verifyData) {
        // OTP verification failed
        const errorMsg =
          verifyError?.message || "Invalid or expired verification code";
        setOtpError(errorMsg);
        setOtpCodeValid(false);
        return;
      }

      // OTP verified successfully - set the new session
      if (verifyData.session) {
        const { error: setSessionError } = await supabase.auth.setSession({
          access_token: verifyData.session.access_token,
          refresh_token: verifyData.session.refresh_token,
        });

        if (setSessionError) {
          console.error(
            "Failed to set session after OTP verification:",
            setSessionError
          );
          setOtpError("Session error. Please try again.");
          setOtpCodeValid(false);
          return;
        }
      }

      // OTP is valid and session is set
      setOtpCodeValid(true);
      setOtpError(null);
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError("Failed to verify code. Please try again.");
      setOtpCodeValid(false);
    }
  };

  /**
   * Resend OTP code
   */
  const resendOTP = async () => {
    setOtpCode("");
    setOtpCodeValid(false);
    await sendOTP();
  };

  /**
   * Reset OTP flow state
   */
  const resetOTPFlow = () => {
    setRequireOTP(false);
    setOtpCodeSent(false);
    setOtpCode("");
    setOtpCodeValid(false);
    setOtpError(null);
  };

  /**
   * Reset code sent state (allows requesting new code without resetting entire flow)
   * Used when code expires
   */
  const resetCodeSent = () => {
    setOtpCodeSent(false);
    setOtpCode("");
    setOtpCodeValid(false);
  };

  /**
   * Start OTP flow (show OTP input)
   */
  const startOTPFlow = () => {
    setRequireOTP(true);
  };

  return {
    // State
    requireOTP,
    otpCodeSent,
    otpCode,
    otpCodeValid,
    sendingOTP,
    otpError,
    // Actions
    sendOTP,
    handleOTPCodeChange,
    resendOTP,
    resetOTPFlow,
    resetCodeSent,
    startOTPFlow,
    setOtpError, // Expose setter for external error handling
  };
};

